/*
 * 인사이트(뉴스·이벤트·블로그) 데이터의 공개 API.
 *
 * 마크업은 renewal 이 갖고, **데이터는 호스트가 준다.** 단독으로 뜰 때는 자체 서버
 * 라우트(`/api/insights`)를 쓰고, 다른 앱에 얹힐 때는 그 앱이 자기 뉴스 API 로
 * 갈아끼운다 — `setRenewalInsightProvider()` 한 번만 호출하면 된다.
 *
 * 이렇게 나눈 이유: 호스트 앱에는 이미 운영 중인 뉴스 API 가 있는데, renewal 의
 * `/api/insights` 는 그 앱에 존재하지 않는다. 컴포넌트가 경로를 직접 알면 얹히는
 * 순간 조용히 빈 목록이 된다.
 */
import { useAsyncData } from '#app'

export interface RenewalInsight {
    /** 이동할 주소. 외부 링크일 수 있다. */
    url: string
    title: string
    /** 뱃지에 쓰는 분류 (뉴스 / 이벤트 / 블로그 카테고리) */
    category: string
    /** `YYYY-MM-DD`. 없으면 빈 문자열 — 컴포넌트가 날짜 줄을 접는다. */
    date: string
    image: string
}

/** 가져올 글의 종류. 자리마다 섞는 범위가 다르다. */
export type RenewalInsightKind = 'news' | 'event'

export const RENEWAL_INSIGHT_ALL: readonly RenewalInsightKind[] = ['news', 'event']

export type RenewalInsightProvider = (
    limit: number,
    kinds: readonly RenewalInsightKind[],
) => Promise<RenewalInsight[]>

/** renewal 단독일 때. 자체 서버 라우트가 insight-links.json 을 읽어 메타데이터를 채운다.
 *  이 라우트는 종류를 구분하지 않으므로 kinds 는 무시한다. */
const defaultProvider: RenewalInsightProvider = async (limit) => {
    const insights = await $fetch<RenewalInsight[]>('/api/insights')
    return Array.isArray(insights) ? insights.slice(0, limit) : []
}

let provider: RenewalInsightProvider = defaultProvider

/**
 * 호스트가 자기 뉴스 API 로 갈아끼운다. 앱이 뜰 때 한 번만 부르면 된다(플러그인 등).
 * 요청마다 달라지는 값이 아니라 앱 설정이므로 모듈 수준에 둔다.
 */
export function setRenewalInsightProvider(next: RenewalInsightProvider) {
    provider = next
}

export function useRenewalInsights(limit = 6, kinds: readonly RenewalInsightKind[] = RENEWAL_INSIGHT_ALL) {
    return useAsyncData<RenewalInsight[]>(
        `renewal-insights-${limit}-${[...kinds].sort().join('+')}`,
        async () => {
            try {
                return await provider(limit, kinds)
            } catch (error) {
                // 뉴스를 못 불러왔다고 페이지가 죽으면 안 된다 — 목록만 접는다.
                // 다만 조용히 비면 원인을 못 찾으므로 로그는 남긴다.
                console.error('[renewal] 인사이트를 불러오지 못했습니다', error)
                return []
            }
        },
        { default: () => [] },
    )
}
